import { ManageBodyOverflow } from '@advanza/modal'
import { Button, Icon } from '@advanza/ui'
import { ReactNode, RefObject, useEffect, useRef, useState } from 'react'
import { CSSTransition } from 'react-transition-group'
import style from './../selectCheckbox.module.css'

export interface SelectCheckboxOption<T extends string | number> {
    value: T
    name?: ReactNode
    after?: ReactNode
    disabled?: boolean
}

interface SelectCheckboxProps<T extends string | number> {
    options: SelectCheckboxOption<T>[]
    value: T | T[] | undefined
    onChangeValue: (values: T[]) => void
    large?: boolean
    wide?: boolean
    error?: boolean
    bottom?: boolean
    question?: ReactNode
    placeholder?: ReactNode
    legend?: boolean
    multiple?: boolean
    rememberQuery?: boolean
    onChangeQuery?: (query: string) => void
    onCloseCheckbox?: () => void
    stickyHeaderAndSearch?: boolean
    customButton?: ReactNode
}

function useOutsideClick(
    ref: RefObject<HTMLDivElement | null>,
    onOutsideClick: (e: MouseEvent) => void,
) {
    const onClick = (e: MouseEvent) => {
        const containerNode = ref && ref.current

        if (
            containerNode &&
            e.target instanceof Node &&
            e.target.isConnected &&
            e.target !== containerNode &&
            !containerNode.contains(e.target)
        ) {
            onOutsideClick(e)
        }
    }

    useEffect(() => {
        window.addEventListener('click', onClick)
        return () => window.removeEventListener('click', onClick)
    }, [])
}

const isMobile = false
const SelectCheckbox = <T extends string | number>({
    options,
    large,
    wide,
    value,
    error,
    bottom,
    question,
    placeholder,
    legend = false,
    onChangeValue,
    multiple = true,
    rememberQuery = false,
    onChangeQuery,
    stickyHeaderAndSearch = false,
    customButton,
    ...rest
}: SelectCheckboxProps<T>) => {
    const values = Array.isArray(value) ? value : ([value].filter(Boolean) as T[])

    const ref = useRef<HTMLDivElement>(null)
    const inputRef = useRef<HTMLInputElement>(null)
    const listEl = useRef<HTMLDivElement>(null)
    const transitionRef = useRef<HTMLDivElement>(null)
    const [maxHeight, setMaxHeight] = useState(0)
    const [minWidth, setMinWidth] = useState(200)
    const [open, setOpen] = useState(false)
    const [query, setQuery] = useState('')

    useEffect(() => {
        if (open) {
            document.addEventListener('keydown', handleKeyPress)
        }

        return () => {
            document.removeEventListener('keydown', handleKeyPress)
        }
    }, [open])

    const calcMaxHeight = () => {
        const { bottom = 0, height = 0 } =
            (ref && ref.current && ref.current.getBoundingClientRect()) || ({} as Partial<DOMRect>)
        if (bottom > 1 && ref.current) {
            const calculatedMaxHeight = document.documentElement.clientHeight - bottom + height
            const maxHeight = Math.max(300, calculatedMaxHeight)
            setMaxHeight(maxHeight - 30)
            setMinWidth(ref.current.clientWidth)
        }
    }

    const onClose = () => {
        setOpen(false)
        rest.onCloseCheckbox && rest.onCloseCheckbox()
        document.removeEventListener('keydown', handleKeyPress)
        if (!rememberQuery) {
            setTimeout(() => setQuery(''), 100)
        }
    }

    useOutsideClick(ref, onClose)

    const activeItem = () => {
        if (!listEl.current) {
            return false
        }
        for (let i = 0; i < listEl.current.childElementCount; i++) {
            if (listEl.current.children[i] === document.activeElement) {
                return i
            }
        }
        return false
    }
    const focusItem = (index: number) => {
        if (!listEl.current) {
            return
        }
        const i = Math.max(0, Math.min(index, filteredOptions.length - 1))
        const item = listEl.current.children[i] as HTMLElement | undefined
        item && item.focus()
    }

    const handleKeyPress = (e: KeyboardEvent) => {
        const activeIndex = activeItem()
        switch (e.key) {
            case 'ArrowDown':
                focusItem(activeIndex === false ? 0 : (activeIndex || 0) + 1)
                e.preventDefault()
                break
            case 'ArrowUp':
                focusItem((activeIndex || 0) - 1)
                e.preventDefault()
                break
            case 'Enter':
                break
            case 'Escape':
                onClose()
                break
            default:
                inputRef.current?.focus()
        }
    }
    const onOpen = () => {
        setOpen(true)
        calcMaxHeight()
    }
    const onChange = (value: T, i: number) => {
        const typedValue = (isNaN(Number(value)) ? value : parseInt(String(value), 10)) as T
        if (multiple === false) {
            onChangeValue([typedValue])
            onClose()
        } else {
            const newValues = values.includes(typedValue)
                ? values.filter((id) => id !== typedValue)
                : values.concat([typedValue])
            onChangeValue(newValues)
        }

        focusItem(i)
        setTimeout(calcMaxHeight)
    }

    const className = [
        style.root,
        bottom ? style.bottom : '',
        large ? style.large : '',
        wide ? style.wide : '',
        values.length === 0 ? style.empty : '',
    ].join(' ')

    const filteredOptions = options.filter(({ name }) => {
        if (!query || query.length < 1 || typeof name !== 'string') {
            return true
        }
        return name.toLowerCase().includes(query.toLowerCase())
    })
    const optionValues = options.map(({ value }) => value)
    const relevantValues = values.filter((val) => optionValues.indexOf(val) !== -1)
    const firstOption = options.filter(({ value }) => value === relevantValues[0])[0] || {}

    const picker = (
        <div className={style.picker} style={{ maxHeight, minWidth: minWidth + 12 }}>
            <div className={stickyHeaderAndSearch ? style.stickyHeaderAndSearch : undefined}>
                <div className={style.header}>
                    <div>{question || placeholder}</div>
                    <button onClick={onClose}>
                        <Icon name="close" />
                    </button>
                </div>
                <input
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value)
                        onChangeQuery && onChangeQuery(e.target.value)
                        if (activeItem() === false) {
                            focusItem(0)
                        }
                    }}
                    ref={inputRef}
                />
            </div>
            <div ref={listEl}>
                {filteredOptions.map(({ value, name, after, disabled }, i) => {
                    const isChecked = values.includes(value) || disabled
                    const icon =
                        multiple === false
                            ? `radio_button_${isChecked ? 'checked' : 'unchecked'}`
                            : `check_box${isChecked ? '' : '_outline_blank'}`
                    return (
                        <div
                            onClick={() => onChange(value, i)}
                            tabIndex={-1}
                            onKeyDown={(e) => e.key === 'Enter' && onChange(value, i)}
                            key={value}
                            className={[
                                style.option,
                                isChecked ? style.checked : '',
                                disabled ? style.disabled : '',
                            ].join(' ')}>
                            <Icon name={icon} />
                            <div>{name}</div>
                            {after && <div>{after}</div>}
                        </div>
                    )
                })}
            </div>
            <div className={style.done} onClick={onClose}>
                <Button name="text">Done</Button>
            </div>
            {isMobile && <ManageBodyOverflow />}
        </div>
    )

    return (
        <div ref={ref} className={className}>
            {legend && <div className={style.legend}>{placeholder}</div>}
            <div onClick={onOpen}>
                {customButton ? (
                    <>{customButton}</>
                ) : (
                    <div className={[style.input, error ? style.hasError : ''].join(' ')}>
                        {values.length === 0 ? (
                            <div className={style.placeholder}>{placeholder}</div>
                        ) : (
                            <div className={style.selectedItems}>
                                <span>
                                    {firstOption.name}{' '}
                                    {relevantValues.length > 1 && (
                                        <span>(+{relevantValues.length - 1} selected)</span>
                                    )}
                                </span>
                            </div>
                        )}
                        <div className={style.selectTriangle} />
                    </div>
                )}
            </div>
            {error && <div className={style.error}>Select option</div>}
            <CSSTransition
                nodeRef={transitionRef}
                classNames={style}
                in={open}
                timeout={200}
                unmountOnExit>
                <div ref={transitionRef}>{picker}</div>
            </CSSTransition>
        </div>
    )
}

export default SelectCheckbox
